import { DailyLocalState } from "./DailyLocalState";

/**
 * Per-tab-session (never persisted across days, never authoritative)
 * bookkeeping for the challenge-attribution/analytics-dedup rules in the
 * PR spec:
 *   - "Keep client challenge context only for the current tab/session and
 *     Daily, not indefinitely across days" - `sessionStorage`, not
 *     `localStorage`, and every read is scoped to today's `challengeId`.
 *   - "If several challenge links are opened, the latest active link in
 *     the current session is the attribution source" - `setActiveSource`
 *     always overwrites.
 *   - "Deduplicate repeated opens/conversions from the same player and
 *     challenge" - `markEventFired`/`hasEventFired` per (event, publicId).
 * None of this is ever read by scoring/attempts/ranking - purely an
 * analytics/attribution convenience, safe to lose entirely (a fresh tab,
 * private browsing, storage disabled) with no effect beyond "this
 * particular open isn't deduplicated/attributed as precisely."
 */

const ACTIVE_SOURCE_KEY = "snake-arena-score-challenge-source-v1";
const FIRED_EVENTS_KEY = "snake-arena-score-challenge-fired-v1";

interface ActiveSourceState {
  challengeId: string;
  publicId: string;
}

function readSessionJson<T>(key: string): T | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    globalThis.sessionStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Session-scoped convenience only - safe to skip on storage failure.
  }
}

export const ScoreChallengeSession = {
  /** Records `publicId` as the current tab's attribution source for `challengeId` - always overwrites any earlier value, per "the latest active link in the current session wins." */
  setActiveSource(challengeId: string, publicId: string): void {
    writeSessionJson(ACTIVE_SOURCE_KEY, { challengeId, publicId } satisfies ActiveSourceState);
  },

  /** The current tab's attribution source for `challengeId` - `null` once the NY date rolls over past it (see the PR spec's "clear or ignore the attribution after New York rollover"), never a stale prior day's token. */
  getActiveSource(challengeId: string): string | null {
    const state = readSessionJson<ActiveSourceState>(ACTIVE_SOURCE_KEY);
    return state && state.challengeId === challengeId ? state.publicId : null;
  },

  /** Whether `eventName` has already been fired for `publicId` in this tab session - callers should skip re-firing (never skip re-RENDERING the UI, only the analytics event) when this is true. */
  hasEventFired(eventName: string, publicId: string): boolean {
    const fired = readSessionJson<string[]>(FIRED_EVENTS_KEY) ?? [];
    return fired.includes(`${eventName}:${publicId}`);
  },

  markEventFired(eventName: string, publicId: string): void {
    const fired = readSessionJson<string[]>(FIRED_EVENTS_KEY) ?? [];
    const key = `${eventName}:${publicId}`;
    if (!fired.includes(key)) writeSessionJson(FIRED_EVENTS_KEY, [...fired, key]);
  },

  /**
   * A best-effort "has this browser played Daily before" signal for the
   * `new_player`/`returning_player` analytics property - reuses
   * `DailyLocalState`'s existing non-authoritative display cache (any prior
   * day's snapshot counts) rather than adding a third storage key for the
   * same rough purpose. Never used for anything authoritative.
   */
  isReturningPlayer(): boolean {
    return DailyLocalState.get() !== null;
  },
};
